import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo.jsx';
import { PageHeader } from '../components/PageHeader.jsx';
import { usePage } from '../hooks/usePage.js';
import { useApi } from '../hooks/useApi.js';
import { fetchFacilities } from '../lib/api.js';

/**
 * Facilities live in the `facilities` table and are admin-managed.
 * The fallback list below mirrors the original hardcoded content so the
 * page still renders if the API is empty / unreachable.
 *
 * `specs` is a JSON array of `{ value, label }` rows shown as the
 * pulled-out highlight numbers under each facility description.
 */
const FALLBACK_FACILITIES = [
  {
    id: 'library',
    eyebrow: 'Spaces',
    title: 'Library',
    image: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=1400&q=80',
    lede: 'A 15,000-volume collection across two campuses, with quiet study zones reserved for IGCSE and IAL students preparing for board examinations.',
    body: 'Open every weekday from 7:30 AM to 6:00 PM, our libraries blend an extensive print collection with a digital catalogue, periodicals, and curated subject bookshelves built by our faculty.',
    specs: [
      { value: '15,000+', label: 'Books' },
      { value: '8',       label: 'Quiet zones' },
      { value: 'Digital', label: 'Catalogue' },
    ],
  },
];

/** Specs may come back as a JSON string from the DB JSON column. */
function parseSpecs(specs) {
  if (Array.isArray(specs)) return specs;
  if (typeof specs === 'string') {
    try { const v = JSON.parse(specs); return Array.isArray(v) ? v : []; }
    catch { return []; }
  }
  return [];
}

function FacilityFeature({ facility, index, total }) {
  const flip = index % 2 === 1;
  const specs = parseSpecs(facility.specs);
  return (
    <section className="fac-feature reveal" data-flip={String(flip)}>
      <div className="container">
        <div className="fac-grid">
          <div className="fac-image">
            <div className="img" style={{ backgroundImage: `url('${facility.image || ''}')` }} />
            <span className="badge">{String(index + 1).padStart(2, '0')}</span>
          </div>
          <div className="fac-copy">
            <span className="eyebrow">{facility.eyebrow} · {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
            <h2 dangerouslySetInnerHTML={{ __html: String(facility.title || '').replace(/(\w+)$/, '<em>$1</em>') }} />
            {facility.lede && <p className="lede">{facility.lede}</p>}
            {facility.body && <p>{facility.body}</p>}
            {specs.length > 0 && (
              <ul className="fac-specs">
                {specs.map((s, i) => (
                  <li key={i}>
                    <strong>{s.value}</strong>
                    <span>{s.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function FacilitiesPage() {
  const head = usePage('facilities');
  const { data: api } = useApi(fetchFacilities, []);
  const facilities = (api && api.length) ? api : FALLBACK_FACILITIES;

  return (
    <>
      <Seo title="Facilities" description={head.metaDescription} />

      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Facilities' }]}
        headline={head.headline}
        lede={head.lede}
        image={head.banner}
      />

      {/* Stats strip */}
      <div className="fac-stats">
        <div className="container">
          <div className="stat">
            <div className="num">{facilities.length}</div>
            <div className="lbl">Facility areas</div>
          </div>
          <div className="stat">
            <div className="num">2</div>
            <div className="lbl">Dhanmondi campuses</div>
          </div>
          <div className="stat">
            <div className="num">15<em>k+</em></div>
            <div className="lbl">Library volumes</div>
          </div>
          <div className="stat">
            <div className="num">400</div>
            <div className="lbl">Seat auditorium</div>
          </div>
        </div>
      </div>

      {/* Editorial intro */}
      <div className="container">
        <div className="fac-intro reveal">
          <span className="section-eyebrow">A tour of the school</span>
          <h2>Every space tells a part of the <em>Maple Leaf</em> story</h2>
          <p>
            From the early-years studios where children first learn to share a room, to the senior science labs where IGCSE practicals are sat — every part of our two Dhanmondi campuses is built around a single purpose: helping students discover, achieve and become more.
          </p>
        </div>
      </div>

      {/* Editorial spread */}
      {facilities.map((facility, idx) => (
        <FacilityFeature
          key={facility.id ?? idx}
          facility={facility}
          index={idx}
          total={facilities.length}
        />
      ))}

      {/* Closing CTA */}
      <section className="fac-cta">
        <div className="container">
          <div className="copy">
            <span className="section-eyebrow">Visit the campus</span>
            <h2>See the spaces <em>for yourself</em></h2>
            <p>
              We welcome prospective families to tour our facilities at any time — alongside the students and teachers who use them every day.
            </p>
          </div>
          <div className="actions">
            <Link to="/contact" className="btn btn-gold">Book a tour</Link>
            <Link to="/admission" className="btn btn-outline">Apply now</Link>
          </div>
        </div>
      </section>
    </>
  );
}
